import { createContext, useContext } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BundleAPIPromiseClient } from "../../gen/witbooking/backoffice/bundle/v1/bundle_api_grpc_web_pb";
import { ExtraAPIPromiseClient } from "../../gen/witbooking/backoffice/extra/v1/extra_api_grpc_web_pb";
import { TagAPIPromiseClient } from "../../gen/witbooking/backoffice/tags/v1/tag_api_grpc_web_pb";
import { useGrpcClient, usePropertyGrpcCall } from "../../lib/grpc";
import { useHttpClient } from "../../lib/http";
import { useProperty } from "../../components/property-provider";
import { useAccommodationApi } from "../accommodation/infrastructure/api/accommodation.api";
import { accommodationQueryKeys } from "../accommodation/accommodation.queryKeys";
import { Tag } from "./domain/Tag";
import { TagAssociation } from "./domain/TagAssociation";
import { TagMultiUpdateResponse } from "./domain/TagMultiUpdateResponse";
import { ApiTagAssociationRepository } from "./infrastructure/ApiTagAssociationRepository";
import { ApiTagRepository } from "./infrastructure/ApiTagRepository";

export interface ContextState {
  getTags: () => Promise<Tag[]>;
  importTags: (propertyTicker: string, file: File) => Promise<TagMultiUpdateResponse>;
  exportTags: (propertyTicker: string) => Promise<Blob>;
  getTagsAssociations: (propertyTicker: string) => Promise<TagAssociation[]>;
  updateTagAssociation: (
    oldTagAssociation: TagAssociation,
    newTagAssociation: TagAssociation
  ) => Promise<void>;
}

const TagContext = createContext({} as ContextState);

export const TagsContextProvider = ({ children }: React.PropsWithChildren) => {
  const client = useHttpClient("", { usePropertyHeader: true });
  const tagAPIClient = useGrpcClient(TagAPIPromiseClient);
  const extraAPIClient = useGrpcClient(ExtraAPIPromiseClient);
  const bundleAPIClient = useGrpcClient(BundleAPIPromiseClient);
  const tagsCall = usePropertyGrpcCall(tagAPIClient, tagAPIClient.getTags);
  const updateTagAppliesToCall = usePropertyGrpcCall(
    tagAPIClient,
    tagAPIClient.updateTagsAppliesTo
  );
  const property = useProperty();
  const queryClient = useQueryClient();
  const { getAccommodations } = useAccommodationApi();
  const getAccommodationsCall = () =>
    queryClient.ensureQueryData({
      queryKey: accommodationQueryKeys.list(property.ticker),
      queryFn: () => getAccommodations(),
      staleTime: 5 * 60 * 1000,
    });
  const getExtrasCall = usePropertyGrpcCall(extraAPIClient, extraAPIClient.getExtras);
  const getBundlesCall = usePropertyGrpcCall(bundleAPIClient, bundleAPIClient.getBundles);

  const tagRepository = new ApiTagRepository(client, tagsCall);
  const tagAssociationRepository = new ApiTagAssociationRepository(
    tagRepository,
    getAccommodationsCall,
    getExtrasCall,
    getBundlesCall,
    updateTagAppliesToCall
  );

  const importTags = (propertyTicker: string, file: File) =>
    tagRepository.importTags(propertyTicker, file);

  const exportTags = (propertyTicker: string) => tagRepository.exportTags(propertyTicker);

  const getTagsAssociations = (propertyTicker: string) =>
    tagAssociationRepository.getAll(propertyTicker);

  const getTags = () => tagRepository.getAll();

  const updateTagAssociation = (
    oldTagAssociation: TagAssociation,
    newTagAssociation: TagAssociation
  ) => tagAssociationRepository.modify(oldTagAssociation, newTagAssociation);

  return (
    <TagContext.Provider
      value={{ importTags, exportTags, getTagsAssociations, getTags, updateTagAssociation }}>
      {children}
    </TagContext.Provider>
  );
};

export const useTagsContext = () => useContext(TagContext);
